import type { components } from '~/generated/api/schema'
import type { AdminTenantAppearanceView, PublishedTenantAppearanceView, TenantAppearanceDocument } from '~/shared-kernel/domain/tenant-appearance'
import { normalizeAppearanceDocument } from '~/shared-kernel/domain/tenant-appearance'
import { apiRequest } from '~/shared-kernel/infrastructure/http/api-client'

type PublishedDto = components['schemas']['PublishedTenantAppearanceResponseDto']
type AdminDto = components['schemas']['AdminTenantAppearanceResponseDto']

function requireConfig(value: unknown): TenantAppearanceDocument {
  const config = normalizeAppearanceDocument(value)
  if (!config) throw new Error('Backend returned an invalid tenant appearance document.')
  return config
}

export async function getPublishedTenantAppearance(): Promise<PublishedTenantAppearanceView> {
  const dto = await apiRequest<PublishedDto>('GET', '/public/appearance')
  return { config: requireConfig(dto.config), version: dto.version }
}

function mapAdmin(dto: AdminDto): AdminTenantAppearanceView {
  return {
    draft: requireConfig(dto.draft),
    published: requireConfig(dto.published),
    version: dto.version,
    publishedAt: dto.publishedAt ?? null
  }
}

export async function getAdminTenantAppearance() {
  return mapAdmin(await apiRequest<AdminDto>('GET', '/admin/appearance', {}, { realm: 'staff', interfaceLoad: 'foreground' }))
}

export async function saveTenantAppearanceDraft(config: TenantAppearanceDocument, expectedVersion: number) {
  return mapAdmin(await apiRequest<AdminDto>('PUT', '/admin/appearance/draft', { body: { config, expectedVersion } }, { realm: 'staff' }))
}

export async function publishTenantAppearance(expectedVersion: number) {
  return mapAdmin(await apiRequest<AdminDto>('POST', '/admin/appearance/publish', { body: { expectedVersion } }, { realm: 'staff' }))
}

export async function resetTenantAppearance(expectedVersion: number) {
  return mapAdmin(await apiRequest<AdminDto>('POST', '/admin/appearance/reset', { body: { expectedVersion } }, { realm: 'staff' }))
}
